import React, { FC } from 'react';
import {
  PrimaryNavigation,
  PrimaryNavigationExpansionItem,
  PrimaryNavigationItem,
  PrimaryNavigationSubItem,
} from '@lifeomic/chroma-react/components/PrimaryNavigation';
import sidebarNavItemsFl from './data/navItems_fl';
import {
  useActiveAccount,
  useActiveProjectId,
  usePolicy,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useIntl } from 'react-intl';
import urls from '../../../util/urls';
import messages from './data/messages';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useSelector } from 'react-redux';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { JupyterNotebooks } from '../icons/JupyterNotebooks';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { Home } from '@lifeomic/chromicons';
import getIsInFedEnvironment from '@lifeomic/phc-web-toolkit/src/util/getIsInFedEnvironment';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import useIsMobile from '@lifeomic/phc-web-toolkit/src/hooks/useIsMobile';
import { testIds as sidebarTestIds } from './index';
import { tiles, testIds as tilesTestIds } from '../../../common/tiles_fl';

const useStyles = makeStyles(theme => ({
  gapLine: {
    height: 1,
    background: theme.palette.black[100],
    margin: theme.spacing(2, -2),
    padding: 0,
  },
}));

export const FountainLifeSidebarNav: FC = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const activeAccount = useActiveAccount();
  const activeProjectId = useActiveProjectId();
  const policy = usePolicy();
  const features = useSelector(featuresSelectors.selectFeatures);
  const isMobile = useIsMobile();

  // Collapse sidebar navigation if user is on mobile
  React.useEffect(() => {
    const isSidebarCollapsed = JSON.parse(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      window.localStorage.getItem('@@lifeomic/store/layoutSidebarCollapsed'),
    );

    if (isMobile && !isSidebarCollapsed) {
      window.localStorage.setItem(
        '@@lifeomic/store/layoutSidebarCollapsed',
        'true',
      );
    }
  }, [isMobile]);

  const filteredSidebarItems = sidebarNavItemsFl.filter(n =>
    n.item.shouldDisplay(activeAccount, policy, features),
  );

  return (
    <PrimaryNavigation>
      {features?.projectLandingPage && activeProjectId && (
        <PrimaryNavigationItem
          to={urls.app.projectLandingPage(activeProjectId)}
          icon={<Home />}
          label={formatMessage(messages.home)}
          key={-1}
        />
      )}
      {filteredSidebarItems.map((n, i) => {
        const Icon = n.item.navIcon || n.item.tileIcon;
        const requiresActiveProject =
          n.requiresActiveProject || n.item.requiresProject;

        if (n.item.id === 'omics') {
          if (requiresActiveProject && !activeProjectId) {
            return null;
          }

          return (
            <PrimaryNavigationExpansionItem
              key={i}
              icon={Icon ? <Icon /> : null}
              to={n.item.uri}
              label={n.item.title}
              rootParentPath={urls.app.omics.root()}
              {...getTestProps(sidebarTestIds.omicsRoot)}
            >
              {tiles.genomicsIngestions.shouldDisplay(
                activeAccount,
                policy,
                features,
              ) && (
                <PrimaryNavigationSubItem
                  to={tiles.genomicsIngestions.uri}
                  label={tiles.genomicsIngestions.title}
                  {...getTestProps(tilesTestIds.genomicsIngestions)}
                />
              )}
              <PrimaryNavigationSubItem
                to={tiles.genomicsTests.uri}
                label={tiles.genomicsTests.title}
                {...getTestProps(tilesTestIds.genomicsTests)}
              />
              {tiles.genomicsActions.shouldDisplay(
                activeAccount,
                policy,
                features,
              ) && (
                <PrimaryNavigationSubItem
                  to={tiles.genomicsActions.uri}
                  label={tiles.genomicsActions.title}
                  {...getTestProps(tilesTestIds.genomicsActions)}
                />
              )}
            </PrimaryNavigationExpansionItem>
          );
        }
        return requiresActiveProject && !activeProjectId ? null : (
          <PrimaryNavigationItem
            key={i}
            to={n.item.uri}
            icon={Icon ? <Icon /> : null}
            label={n.item.title}
            {...getTestProps(n.item.testId)}
          />
        );
      })}
      {filteredSidebarItems.length > 0 && <li className={classes.gapLine}></li>}
      <EnforceAccountFeatureToggle accountFeature="notebooks">
        {activeAccount &&
          !getIsInFedEnvironment() &&
          activeAccount.type === 'ENTERPRISE' &&
          activeAccount.products?.includes('Notebooks') && (
            <PrimaryNavigationItem
              to={urls.jupyterNotebooks()}
              icon={<JupyterNotebooks />}
              label={formatMessage(messages.notebooksLabel)}
              {...getTestProps(sidebarTestIds.notebooks)}
            />
          )}
      </EnforceAccountFeatureToggle>
    </PrimaryNavigation>
  );
};

FountainLifeSidebarNav.displayName = 'FountainLifeSidebarNav';
export default FountainLifeSidebarNav;
